import * as styles from "./DogPage.module.scss";

import {
	shareOnFB,
	shareOnX,
	shareViaEmail,
	shareViaWhatsApp,
} from "@dogstrust/dt-web-core-lib-front-end/src/utils/socialMedia";

import { type PageProps, graphql } from "gatsby";
import React, { lazy, useCallback, useEffect, useMemo, useState } from "react";

import { DogBioLinks } from "@dogstrust/dt-web-core-lib-front-end/src/components/DogBioLinks";
import SSRSuspense from "@dogstrust/dt-web-core-lib-front-end/src/components/SSRSuspense/SSRSuspense";
import { StandardSignPostCard } from "@dogstrust/dt-web-core-lib-front-end/src/components/SignPostCards/StandardSignPostCard";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import WrappedSection from "@dogstrust/dt-web-core-lib-front-end/src/containers/WrappedSection/WrappedSection";
import IntroTripleCarousel from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroTripleCarousel";
import SectionCentreDetails from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionCentreDetails";
import { getSectionCentreDetailsProps } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionCentreDetails/SectionCentreDetails";
import SectionCentrePromotedDogs from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionCentrePromotedDogs";
import SectionDogBio from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionDogBio";
import SectionStandardSignPostCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionStandardSignPostCards";
import SectionSteppedProcess from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionSteppedProcess";
import { getSectionSteppedProcessProps } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionSteppedProcess/SectionSteppedProcess";
import SectionTitleBody from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionTitleBody";
import {
	useDogSearchApi,
	usePageManager,
} from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import SectionRehomingCentreKeyDetails from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionRehomingCentreKeyDetails";
import { getRehomingCentreKeyDetailsProps } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionRehomingCentreKeyDetails/RehomingCentreKeyDetails";
import { usePersistState } from "@dogstrust/dt-web-core-lib-front-end/src/hooks/usePersistState";
import useWindowLocationHref from "@dogstrust/dt-web-core-lib-front-end/src/hooks/useWindowLocationHref";
import { uniqueSet } from "@dogstrust/dt-web-core-lib-front-end/src/utils/array";
import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getDogAgeBand } from "@dogstrust/dt-web-core-lib-front-end/src/utils/dates";
import { getDogHeroImage } from "@dogstrust/dt-web-core-lib-front-end/src/utils/dog";
import {
	getDogMedia,
	mapArticleToSignpostCard,
} from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { parseStringTokens } from "@dogstrust/dt-web-core-lib-front-end/src/utils/text";
import { parseDogNode } from "@dogstrust/dt-web-core-lib-front-end/src/utils/utils";

const FilterPanel = lazy(
	() =>
		import(
			"@dogstrust/dt-web-core-lib-front-end/src/containers/filters/FilterPanel"
		),
);

interface Props extends PageProps {
	data: Queries.DogQueryQuery;
}

export const Head: React.FC<Props> = ({ data }) => {
	const dogData: DogsTrust.DogData = useMemo(() => parseDogNode(data), [data]);

	// Metadata
	const title = `Adopt a ${dogData.frontEndBreedName} Rescue Dog | ${dogData.name} | Dogs Trust`;
	const metaDescription = `${dogData.name}, the ${dogData.frontEndBreedName} Rescue Dog is looking for a new home. Find out more about ${dogData.name} and all of our dogs for rehoming today.`;
	return (
		<DefaultHead pageUrl={dogData.url}>
			<SEO
				{...{
					title: title,
					description: metaDescription,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + dogData.url,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + dogData.url,
					og_title: title,
					og_description: metaDescription,
					og_image_url: `${process.env.GATSBY_DOMAIN_NAME}/images/400x300/${
						getDogHeroImage(data.dogData)?.src
					}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: metaDescription,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: title,
					twitter_cards_page_url: dogData.url,
					twitter_cards_image: `${
						process.env.GATSBY_DOMAIN_NAME
					}/images/400x300/${getDogHeroImage(data.dogData)?.src}`,
				}}
			/>
		</DefaultHead>
	);
};

export const DogPage: React.FC<Props> = ({ data, location }) => {
	const { setContentWidth, deregisterPage, usePagination, getDogIndex } =
		usePageManager();

	const dogData: DogsTrust.DogData = useMemo(() => parseDogNode(data), [data]);
	interface LocationWithPagination {
		pagination: DogsTrust.PaginationProps;
	}

	const pagination = usePersistState<DogsTrust.PaginationProps>(
		"pagination",
		(
			(location?.state as LocationWithPagination) ||
			({} as LocationWithPagination)
		).pagination,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once on page load
	useEffect(() => {
		setContentWidth("DEFAULT");
		return () => {
			deregisterPage();
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once on page load
	useEffect(() => {
		if (pagination) {
			usePagination(true);
			getDogIndex(window.location.pathname);
		}
	}, [pagination]);

	const {
		allNodeCentres: { nodes: allCentres },
		steppedProcess,
		dogAdviceArticles,
	} = data;
	const [hasInit, setHasInit] = useState(false);
	const { thisDogsCentre, breadcrumbs } = data.dogData;
	const { thisDogsBreedPage, ...relatedPages } = data.dogData.relatedPages;

	const advicePages = uniqueSet(
		Object.values(relatedPages).filter((page) => !!page),
	);

	const { onInitFilters, centreNames } = useDogSearchApi();
	const { windowLocationHref } = useWindowLocationHref();
	useEffect(() => {
		if (!hasInit) {
			const mappedCentres: DogsTrust.RehomingCentreLocation[] = allCentres.map(
				(centre) => ({
					title: centre.title,
					centreCode: centre.relationships.field_centre_code.name,
					latitude: Number.parseFloat(centre.field_latitude),
					longitude: Number.parseFloat(centre.field_longitude),
				}),
			);
			onInitFilters(false, false, mappedCentres, dogData.centreCode);
			setHasInit(true);
		}
	}, [hasInit, onInitFilters, allCentres, dogData.centreCode]);

	const parseSteppedProcess = useCallback((): Queries.node__stepped_process => {
		const dogBioSteppedProcess = {
			...(steppedProcess as Queries.node__stepped_process),
		};
		// This is unique to the dog bio page process. The link is the enquiry form of the given centre
		const firstStep = {
			...dogBioSteppedProcess.relationships.field_step[0],
			field_step_link: {
				uri:
					thisDogsCentre?.field_dog_adoption_form ||
					"/awaiting-enquiry-form-for-this-centre",
				title: "Apply Now",
			} as Queries.field_link,
		};
		// Should be the first result - the stepped process content item should be unique to the bio page with a unique component name "Dog-Bio-Rehoming-Process"
		return {
			...dogBioSteppedProcess,
			field_stepped_intro_text:
				steppedProcess.field_stepped_intro_text &&
				parseStringTokens(`<p>${steppedProcess.field_stepped_intro_text}</p>`, {
					"{DogName}": `<b>${dogData.name}</b>`,
				}),
			relationships: {
				...dogBioSteppedProcess.relationships,
				field_step: [
					firstStep as Queries.paragraph__step,
					...(dogBioSteppedProcess.relationships.field_step.slice(
						1,
					) as Queries.paragraph__step[]), // Remove first step as we've modified it
				],
			},
		};
	}, [steppedProcess, dogData.name, thisDogsCentre]);

	const mappedBreadcrumbs = breadcrumbs?.map((breadCrumb) => {
		const queryStrings = (location?.state as { queryStrings: string[] })
			?.queryStrings;
		if (breadCrumb?.title === "Dogs" && !!queryStrings) {
			return {
				title: breadCrumb.title,
				url: `${breadCrumb.url}${queryStrings}`,
			};
		}
		return breadCrumb;
	});

	return (
		<Layout breadcrumbs={mappedBreadcrumbs ? [...mappedBreadcrumbs] : []}>
			<div className={styles.contentarea}>
				<IntroTripleCarousel
					media={getDogMedia({
						...dogData,
						centreName: thisDogsCentre?.title,
					})}
				/>
				<SectionDogBio
					data={dogData}
					userLocation={
						(location?.state as { userLocation: DogsTrust.Place })?.userLocation
					}
					centreName={thisDogsCentre?.title}
					adoptionFormUrl={thisDogsCentre?.field_dog_adoption_form}
				/>
				<div className={styles.doginfosections}>
					{dogData.likesDislikes && (
						<SectionTitleBody
							title={`About ${dogData.name}`}
							body={dogData.likesDislikes}
						/>
					)}
					{dogData.homeRequirements && (
						<SectionTitleBody
							title={`Are you right for ${dogData.name}?`}
							body={dogData.homeRequirements}
							withBreak={true}
						/>
					)}
					{dogData.info && (
						<SectionTitleBody
							title={`Is ${dogData.name} right for you?`}
							body={dogData.info}
							withBreak={true}
						/>
					)}
				</div>

				{thisDogsBreedPage && (
					<WrappedSection
						sectionName="SectionDogProfileBreed"
						sectionColour={Colours.SANDY_FUR}
					>
						<div className={styles.dogprofilebreed}>
							<StandardSignPostCard
								title={thisDogsBreedPage?.title}
								body={thisDogsBreedPage.field_intro_copy}
								buttonUrl={thisDogsBreedPage.path.alias}
								buttonLabel={"Read more"}
								buttonVariant="secondary"
								imgSrc={
									thisDogsBreedPage?.relationships?.field_featured_image
										?.field_media_image?.uri
								}
								imgAlt={
									thisDogsBreedPage?.relationships?.field_featured_image
										?.field_media_image?.alt
								}
								isOrphan
							/>
						</div>
					</WrappedSection>
				)}
				<WrappedSection sectionName="SectionDogProfileShare">
					<div className={styles.mobileenquireorshare}>
						<DogBioLinks
							{...dogData}
							dog={dogData}
							id={dogData.key.toString()}
							adoptionFormUrl={thisDogsCentre?.field_dog_adoption_form}
							xUrl={encodeURI(shareOnX(windowLocationHref, dogData.name))}
							facebookUrl={encodeURI(
								shareOnFB(windowLocationHref, dogData.name),
							)}
							mailToUrl={encodeURI(
								shareViaEmail(
									windowLocationHref,
									dogData.name,
									dogData.frontEndBreedName,
								),
							)}
							whatsAppUrl={encodeURI(
								shareViaWhatsApp(
									windowLocationHref,
									dogData.name,
									dogData.frontEndBreedName,
								),
							)}
							dogDataTracking={{
								rehoming_name: dogData.name, // name of dog added to favourites
								rehoming_location: thisDogsCentre?.title, // location of dog added to favourites
								rehoming_breed: dogData.frontEndBreedName, // dog breed of dog added to favourites
								rehoming_age: getDogAgeBand(dogData.dob), // dog age of dog added to favourites
								rehoming_size: dogData.size, // dog size of dog added to favourites
								rehoming_gender: dogData.gender === "M" ? "Male" : "Female", // gender
							}}
						/>
					</div>
				</WrappedSection>

				<SectionSteppedProcess
					{...getSectionSteppedProcessProps(parseSteppedProcess())}
				/>
				<SectionRehomingCentreKeyDetails
					{...getRehomingCentreKeyDetailsProps(
						thisDogsCentre as Queries.RehomingCentreDataFragment,
					)}
				/>

				{dogAdviceArticles && advicePages.length > 0 && (
					<SectionStandardSignPostCards
						key={dogAdviceArticles.title}
						title={dogAdviceArticles.field_front_end_title}
						bodyHtmlText={
							dogAdviceArticles.field_section_short_description.value
						}
						items={advicePages.map(mapArticleToSignpostCard)}
					/>
				)}
				<SectionCentrePromotedDogs
					currentDogId={dogData.key.toString()}
					centre={
						centreNames ? centreNames[dogData.centreCode] : dogData.centreCode
					}
					centreCode={dogData.centreCode}
				/>
			</div>
			<SSRSuspense>
				<FilterPanel shouldInit={false} allCentres={[]} isListingPage={false} />
			</SSRSuspense>
		</Layout>
	);
};
export default DogPage;

export const query = graphql`
	
    query DogQuery($path: String) {
        dogData: nodeDog(path: { alias: { eq: $path } }, status: { eq: true }) {
            ...Dog
        }
        allNodeCentres: allNodeCentre {
            nodes {
                title
                field_latitude
                field_longitude
                relationships {
                    field_centre_code {
                        name
                    }
                }
            }
        }
        steppedProcess: nodeSteppedProcess(
            relationships: {
                field_component_type: {
                    name: { eq: "Dog-Bio-Rehoming-Process" }
                }
            }
        ) {
            title
            field_stepped_intro_text
            relationships {
                field_step {
                    field_step_title
                    field_step_intro_text {
                        value
                    }
                    field_step_link {
                        title
                        uri
                    }
                }
            }
        }
        dogAdviceArticles: nodeSection(
            title: { eq: "Section-Dynamic-Dog-Advice-Articles" }
        ) {
            title
            field_front_end_title
            field_section_short_description {
                value
            }
        }
    }
`;

export const fragment = graphql`
    fragment Dog on node__dog {
        __typename
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        apiKey
        field_dog_external_creation_date
        field_dog_gender
        field_dog_image_base64
        field_dog_key
        field_dog_with_cats
        field_dog_with_dogs
        field_dog_with_preschool
        field_dog_with_primary
        field_dog_with_secondary
        field_info
        field_other
        field_likesdislikes
        field_typeofhomerequired
        field_needs_medical_care
        field_iscrossbreed
        field_ishomefromhome
        field_isreserved
        field_isunderdog
        id
        field_dob
        frontEndBreedName
        media {
            images {
                src
                alt
                weight
                isHero
                caption
            }
            videos {
                url
                title
            }
        }
        thisDogsCentre {
            id
            title
            moderation_state
            path {
                alias
            }
            field_address_line_one
            field_address_line_two
            field_city
            field_county
            field_dog_adoption_form
            field_facebook_url
            field_google_maps_link
            field_instagram_url
            field_is_virtual_centre
            field_latitude
            field_linkedin_url
            field_longitude
            field_opening_times_text
            field_phone_number
            field_postcode
            field_twitter_url
            field_centre_lead_paragraph {
                value
            }
            field_centre_intro_text {
                value
            }
            field_wheelchair_text
            relationships {
                field_centre_code {
                    name
                }
                field_centre_images {
                    field_media_image {
                        uri
                        alt
                    }
                }
                field_centre_intro_image {
                    field_caption
                    field_media_image {
                        uri
                        alt
                    }
                }
                field_promotion_blocks {
                    field_title_front_end
                    field_text
                    field_promo_link {
                        uri
                        title
                    }
                    relationships {
                        field_promo_image {
                            field_media_image {
                                uri
                                alt
                            }
                        }
                        field_promotion_type {
                            name
                        }
                    }
                }
            }
        }
        relatedPages {
            thisDogsBreedPage {
                title
                path {
                    alias
                }
                field_intro_copy
                field_breeds_button_text
                relationships {
                    field_featured_image {
                        field_media_image {
                            uri
                            alt
                        }
                    }
                }
            }
            livesWithCats {
                ...ArticleSignPostData
            }
            livesWithDogs {
                ...ArticleSignPostData
            }
            livesWithPreSchool {
                ...ArticleSignPostData
            }
            livesWithPrimarySchool {
                ...ArticleSignPostData
            }
            livesWithSecondarySchool {
                ...ArticleSignPostData
            }
            needsMedicalCare {
                ...ArticleSignPostData
            }
            isReserved {
                ...ArticleSignPostData
            }
            isUnderdog {
                ...ArticleSignPostData
            }
            isHomeFromHome {
                ...ArticleSignPostData
            }
            dogSizeArticle {
                ...ArticleSignPostData
            }
            dogAgeArticle {
                ...ArticleSignPostData
            }
        }

        relationships {
            field_dog_age_band {
                name
            }
            field_crossbreed {
                name
            }
            field_dog_breed {
                name
            }
            field_crossbreed {
                name
            }
            field_dog_dog_size {
                name
            }
            field_dog_location {
                name
            }
            field_dog_status {
                name
            }
        }
        status
        title
    }

    fragment DogSignPost on node__dog {
        status
        title
        path {
            alias
        }
        field_dog_gender
        field_dog_image_base64
        field_dog_key
        field_dog_with_cats
        field_dog_with_dogs
        field_dog_with_preschool
        field_dog_with_primary
        field_dog_with_secondary
        field_info
        field_other
        field_likesdislikes
        field_typeofhomerequired
        field_needs_medical_care
        field_iscrossbreed
        field_ishomefromhome
        field_isunderdog
        field_dob
        frontEndBreedName
        media {
            images {
                src
                alt
                weight
                isHero
                caption
            }
            videos {
                url
                title
            }
        }
        thisDogsCentre {
            title
        }
        relationships {
            field_dog_age_band {
                name
            }
            field_dog_breed {
                name
            }
            field_dog_dog_size {
                name
            }
            field_dog_location {
                name
            }
            field_dog_status {
                name
            }
        }
    }
`;
